import React, {useEffect, useState} from "react";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import LoginUI from "./LoginUI";
import Box from "@mui/material/Box";
import {
  LOGIN_CONTENT_ENDPOINT,
} from "commonUtils/globalAPISettings";
import {getRequest, setHeader} from "repository/genericRepository";
import {store, useAppDispatch, useAppSelector} from "../../redux/store";
import {Loader} from "components/FormComponents/Loader/Loader";
import {isValidObject, loggedMessage} from "commonUtils/utils";
import {clearAuthState, updateBrowserCompatibilityWarningDetails} from "../../redux/reducers/authSlice";
import type {menu} from "../../redux/reducers/authSlice";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import {
  AxiosErrorCode,
  CPSConstants,
  CPSMessages,
  HttpStatusCode,
  PageNames,
  LoginType,
  API_TIMEOUTS_MILLISECONDS,
} from "commonUtils/enumerations";
import LoginBackGroundImage from "../../assets/login_background_image.webp";
import {logMessage} from "services/loggerService";
import {userLogin} from "../../redux/reducers/authAction";
import {useNavigate} from "react-router-dom";
import {encrypt, decrypt} from "../../commonUtils/crypto";
import {clearlogs, postLogsToAPI} from "../../redux/reducers/loggerSlice";
import CPSLogo from "../../assets/cps_logo_white.webp";
import DOMPurify from "dompurify";
import { resetBrowserCompatibilityWarningSession } from "components/Dialogs/BrowserCompatibilityWarningDialog";
interface LoginContentProps {
  privacy: string;
  termsOfUse: string;
  lillyLogin: string;
  usAccessibility: string;
  caAccessibility: string;
  euAccessibility: string;
  chpNotice: string;
  cookiePrivacy: string;
  cookieSetting: string;
  demoSite: string;
  demoStripMessage: string;
  isDeploymentModeOn: boolean;
  cpsLoginMessage: string;
  deploymentMessage: string;
  maintenanceMessage: string;
  showBrowserCompatibilityWarning?: boolean;
  supportedBrowserNames?: string;
  minimumBrowserVersions?: string;
  browserCompatibilityMessage?: string;
  browserCompatibilityTitle?: string;
}

const LoginMaintenance: React.FC = () => {
  const [content, setLoginContent] = useState<LoginContentProps>({
    privacy: "",
    termsOfUse: "",
    lillyLogin: "",
    usAccessibility: "",
    caAccessibility: "",
    euAccessibility: "",
    chpNotice: "",
    cookiePrivacy: "",
    cookieSetting: "",
    demoSite: "",
    demoStripMessage: "",
    isDeploymentModeOn: false,
    cpsLoginMessage: "",
    deploymentMessage: "",
    maintenanceMessage: "",
  });
  const [pwonly, setPwOnly] = useState<string | null>("");
  const [showhide, setShowHide] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [errorContentMsg, setErrorMsg] = React.useState<string>("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  var fromPage = "";
  const hasSwitchApplicationParams = React.useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.has("userIdentity") && searchParams.has("userId");
  }, []);

  const logs = useAppSelector(state => {
    return state.logs;
  });

  const getLoginContent = React.useMemo(
    () => async () => {
      try {
        setLoader(true);
        const APIHeaders = {
          Authorization: `Bearer ${process.env.REACT_APP_API_AUTH_TOKEN}`,
        };
        const responseData = await getRequest({
          url: LOGIN_CONTENT_ENDPOINT,
          headers: setHeader(APIHeaders),
          timeout: API_TIMEOUTS_MILLISECONDS.LOGIN_CONTENT_ENDPOINT,
          params: {},
        });
        if (isValidObject(responseData)) {
          setShowHide(true);
          const payLoad = {
                     BrowserUpgradeRecommendedTitle: responseData.browserUpgradeRecommendedTitle,
                     DefaultBrowserVersions : responseData.defaultBrowserVersions,
                     EdgeDownloadUrl: responseData.edgeDownloadUrl,
                     SafariDownloadUrl: responseData.safariDownloadUrl                    
                  };                  
              dispatch(updateBrowserCompatibilityWarningDetails(payLoad));
          setLoginContent(responseData);
        } else {
          setShowHide(false);
        }
        setLoader(false);
      } catch (error: any) {
        loggedMessage(error, "", PageNames.MAINTENANCE);
        setLoader(false);
        handleAxiosErrors(error);
      }
    },
    [content],
  );
  const loginWithSSO = () => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("FromPage")) {
      fromPage = searchParams.get("FromPage") ?? "";
      fromPage = decrypt(fromPage.replace(/ /g, "+"));
    }

    if (
      searchParams.has("SSOUserName") &&
      searchParams.has("SSOValue") &&
      searchParams.has("OktaLogin")
    ) {
      const userIdSSO = searchParams.get("SSOUserName") ?? "";
      const passwordSSO = searchParams.get("SSOValue") ?? "";
      const loginTypeId = LoginType.OktaSSO;
      ssoLogin({
        userIdentity: userIdSSO.replace(/ /g, "+"),
        password: passwordSSO.replace(/ /g, "+"),
        loginTypeId,
        isEncrypted: true,
      });
    } else if (
      searchParams.has("SSOPeriod") &&
      searchParams.has("SSOUserName") &&
      searchParams.has("SSOValue") &&
      fromPage === CPSConstants.CHANGEPASSWORDFROMEMAIL
    ) {
      const encryptedSSOPeriod = searchParams.get("SSOPeriod") ?? "";
      const SSOPeriod = decrypt(encryptedSSOPeriod.replace(/ /g, "+"));
      const encryptedSSOUserName = searchParams.get("SSOUserName") ?? "";
      const encryptedSSOValue = searchParams.get("SSOValue") ?? "";

      const currentUTCDateTime = new Date(new Date().toUTCString());

      const userIdSSO = decrypt(encryptedSSOUserName.replace(/ /g, "+"));
      const passwordSSO = decrypt(encryptedSSOValue.replace(/ /g, "+"));
      const loginTypeId = LoginType.Application;

      if (
        userIdSSO &&
        passwordSSO &&
        new Date(SSOPeriod) > currentUTCDateTime
      ) {
        ssoLogin({
          userIdentity: userIdSSO,
          password: passwordSSO,
          loginTypeId,
          isEncrypted: false,
        });
      } else {
        setErrorMsg(CPSMessages.LINK_INACTIVE);
        setTimeout(() => {
          getLoginContent();
        }, 1);
      }
    } else if (
      searchParams.has("SSOUserName") &&
      searchParams.has("SSOValue") &&
      fromPage === ""
    ) {
      const userIdSSO = searchParams.get("SSOUserName") ?? "";
      const passwordSSO = searchParams.get("SSOValue") ?? "";
      const loginTypeId = LoginType.LillyNetSSO;
      ssoLogin({
        userIdentity: userIdSSO.replace(/ /g, "+"),
        password: passwordSSO.replace(/ /g, "+"),
        loginTypeId,
        isEncrypted: true,
      });
    } else {
      getLoginContent();
    }
  };
  useEffect(() => {
    try {
      resetBrowserCompatibilityWarningSession();
      if (!hasSwitchApplicationParams) {
        dispatch(clearAuthState());
      }
      loginWithSSO();

      const url = new URLSearchParams(window.location.search);
      for (const key of url.keys()) {
        setPwOnly(key);
      }
    } catch (error: any) {
      setLoader(false);
      setErrorMsg(error.message);
    }
  }, [dispatch, hasSwitchApplicationParams]);

  const ssoLogin = ({
    userIdentity,
    password,
    loginTypeId,
    isEncrypted,
  }: any) => {
    try {
      const loginDetails = {
        userIdentity,
        password,
        isEncrypted,
        loginTypeId,
      };
      dispatch(userLogin(loginDetails))
        .unwrap()
        .then(() => {
          const state = store.getState();
          try {
            if (state?.auth?.authDetails.isAuthenticated) {
              logMessage("INFO", "Login successfull.", PageNames.LOGIN);
              dispatch(
                postLogsToAPI({
                  event: logs,
                  token: state?.auth?.authDetails?.token,
                }),
              );
              dispatch(clearlogs());
              if (state?.auth?.userDetails?.isMultipleProfiles) {
                navigate("/userprofile");
              } else if (state?.auth?.userDetails?.isChangePassword) {
                navigate("/changepwd", {
                  state: {
                    data: {
                      isChangePwdLink:
                        fromPage === CPSConstants.CHANGEPASSWORDFROMEMAIL,
                      passwordSSO: password,
                    },
                  },
                });
              } else if (state?.auth?.userDetails?.isFirstTime) {
                window.location.href = "/firstlogin";
              } else {
                let isNew = true;
                const menus: menu[] =
                  state?.auth?.userPreferences?.menus?.filter(function (
                    id: menu,
                  ) {
                    return (
                      id.pageToLoad === state?.auth?.userDetails?.pageToLoad
                    );
                  });
                if (isValidObject(menus) && menus.length > 0) {
                  isNew = menus[0].isNew;
                }
                if (isNew) {
                  navigate("/" + state?.auth?.userDetails?.pageToLoad);
                }
              }
            } else {
              setErrorMsg(state?.auth?.userDetails?.message);
            }
          } catch (error: any) {
            setLoader(false);
            setErrorMsg(state?.auth?.userDetails?.message);
          }
        })
        .catch(error => {
          handleAxiosErrors(error);
          setLoader(false);
          if (fromPage === CPSConstants.CHANGEPASSWORDFROMEMAIL) {
            setErrorMsg(CPSMessages.LINK_INACTIVE);
          }
          //call loginContent after state changes.
          setTimeout(() => {
            getLoginContent();
          }, 1);
        });
    } catch (error: any) {
      setLoader(false);
      handleAxiosErrors(error);
    }
  };
  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.DASHBOARD};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      setErrorMsg(error.message);
    }
  };

  const defaultTheme = createTheme();

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Loader open={loader} />
      {content?.isDeploymentModeOn
        ? pwonly === CPSConstants.PW_ONLY
          ? showhide && (
            <LoginUI
              errorContentMsg={errorContentMsg}
              contentProps={content}
            />
          )
          : showhide && (
            <>
              <Box
                style={{
                  backgroundImage: `url(${LoginBackGroundImage})`,
                }}
                className="maintainance-background-image-container">
                <Container className="maintainance-content-container">
                  <Box className="maintainance-logo-parent">
                    <img
                      src={CPSLogo}
                      alt="CPS Logo"
                      className="maintainance-logo"
                    />
                  </Box>
                  <Box
                    className="maintainance-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(content?.deploymentMessage),
                    }}
                  />
                </Container>
              </Box>
            </>
          )
        : showhide && (
          <LoginUI errorContentMsg={errorContentMsg} contentProps={content} />
        )}
    </ThemeProvider>
  );
};

export default LoginMaintenance;
